import {
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Input,
  InputAdornment,
} from "@mui/material";
import { Close, FilterList, Search } from "@mui/icons-material";
import clsx from "clsx";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { t } from "ttag";
import ErrorBox from "../../components/WarnningBox/ErrorBox";
import { Lookup } from "../../components/Lookup";
import { EditIcon } from "../../iconImages";
import { Delete } from "../../icons";
import { useStoreActions } from "../../models/RootStore";
import ServiceNavigationBar from "../../widgets/Menu/ServiceNavigationBar";
import AddEditModbusTemplate from "./AddEditModbusTemplate";
import useStyles from "./ModbusTemplateManagement.style";

export const TEMPLATE_TYPE_OPTIONS = [
  { value: "ui", label: t`UI` },
  { value: "functionalUnit", label: t`Functional Unit` },
  { value: "device", label: t`Device` },
];

export const TEMPLATE_TYPE_LABELS: { [key: string]: string } = TEMPLATE_TYPE_OPTIONS.reduce(
  (labels, option) => ({ ...labels, [option.value]: option.label }),
  {}
);

const normalizeTemplate = (template: any) => ({
  ...template,
  id: template.id || template._id,
});

const ModbusTemplateManagement = (props: any) => {
  const styles: any = useStyles();
  const [templates, setTemplates] = useState<any>({});
  const [openAddEdit, setOpenAddEdit] = useState<any>(null);
  const [deleteId, setDeleteId] = useState<any>(null);
  const [searchValue, setSearchValue] = useState<any>("");
  const [filteredTemplates, setFilteredTemplates] = useState<any>([]);
  const [typeFilter, setTypeFilter] = useState<any>([]);
  const [lookupAnchor, setLookupAnchor] = useState<any>(null);

  const getAllModbusTemplates = useStoreActions((action) => action.modbusTemplates.getAllModbusTemplates);
  const deleteModbusTemplate = useStoreActions((action) => action.modbusTemplates.deleteModbusTemplate);
  const { addMessage } = useStoreActions((action) => action.errorMessage);

  const fetchTemplates = () => {
    getAllModbusTemplates()
      .then((res: any) => {
        const list = Array.isArray(res) ? res : Object.values(res || {});
        const byId = list.reduce((all: any, template: any) => {
          const normalized = normalizeTemplate(template);
          all[normalized.id] = normalized;
          return all;
        }, {});
        setTemplates(byId);
      })
      .catch((err: any) => addMessage({ message: err.message }));
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    const search = searchValue.toUpperCase();
    const filtered = Object.values(templates).filter((template: any) => {
      if (typeFilter.length && !typeFilter.includes(TEMPLATE_TYPE_LABELS[template.type] || template.type)) {
        return false;
      }
      const name = (template.name || "").toUpperCase();
      const manufacturer = (template.manufacturer || "").toUpperCase();
      const model = (template.model || "").toUpperCase();
      return !search || name.indexOf(search) >= 0 || manufacturer.indexOf(search) >= 0 || model.indexOf(search) >= 0;
    });
    setFilteredTemplates(filtered);
  }, [searchValue, templates, typeFilter]);

  const handleDelete = () => {
    deleteModbusTemplate(deleteId)
      .then(() => {
        const { [deleteId]: toDelete, ...restOfTemplates } = templates;
        setTemplates(restOfTemplates);
      })
      .catch((err: any) => {
        addMessage({ message: err.message });
      });
    setDeleteId(null);
  };

  const onTemplateSaved = (template: any) => {
    const normalized = normalizeTemplate(template);
    setTemplates({ ...templates, [normalized.id]: normalized });
    setOpenAddEdit(null);
  };

  return (
    <>
      <ServiceNavigationBar
        title={t`Modbus Template Management`}
        searchComponent={
          <Input
            placeholder={t`Search by name, manufacturer or model`}
            value={searchValue}
            onChange={(event: any) => setSearchValue(event.target.value)}
            disableUnderline={true}
            classes={{ root: styles.inputRoot }}
            startAdornment={
              <InputAdornment position="start" className={styles.searchAdorment}>
                <Search className={styles.searchIcon} />
              </InputAdornment>
            }
            endAdornment={
              searchValue && (
                <IconButton
                  onClick={() => setSearchValue("")}
                  className={styles.closeIconStyle}
                >
                  <Close />
                </IconButton>
              )
            }
          />
        }
        {...props}
      >
        <div className={styles.addButtonContainer}>
          <Button
            className={styles.buttonStyle}
            onClick={() => setOpenAddEdit("new")}
          >{t`ADD NEW TEMPLATE`}</Button>
        </div>
        <Paper elevation={0} className={styles.paperPadding}>
          <TableContainer>
            <Table stickyHeader aria-label="modbus templates table">
              <TableHead className={styles.tableHead}>
                <TableRow>
                  <TableCell
                    className={styles.headCells}
                    onClick={(event: any) => setLookupAnchor(event.currentTarget)}
                  >
                    <div className={styles.headContainer}>
                      {t`TYPE`}
                      <FilterList
                        className={clsx(styles.filterStyle, { [styles.blueFilter]: typeFilter.length })}
                      />
                    </div>
                  </TableCell>
                  <TableCell className={styles.headCells}>{t`NAME`}</TableCell>
                  <TableCell className={styles.headCells}>{t`DESCRIPTION`}</TableCell>
                  <TableCell className={styles.headCells}>{t`MANUFACTURER`}</TableCell>
                  <TableCell className={styles.headCells}>{t`MODEL`}</TableCell>
                  <TableCell align="center" className={styles.headCells}>{t`EDIT`}</TableCell>
                  <TableCell align="center" className={styles.headCells}>{t`DELETE`}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {_.orderBy(filteredTemplates, [(template: any) => (template.name || "").toLowerCase()], ["asc"]).map((template: any) => {
                  return (
                    <TableRow hover tabIndex={-1} key={template.id}>
                      <TableCell component="th" scope="row" align="left" className={styles.rowCell}>
                        {TEMPLATE_TYPE_LABELS[template.type] || template.type}
                      </TableCell>
                      <TableCell component="th" scope="row" align="left" className={styles.rowCell}>
                        {template.name}
                      </TableCell>
                      <TableCell component="th" scope="row" align="left" className={styles.rowCell}>
                        {template.description || '-'}
                      </TableCell>
                      <TableCell component="th" scope="row" align="left" className={styles.rowCell}>
                        {template.manufacturer || '-'}
                      </TableCell>
                      <TableCell component="th" scope="row" align="left" className={styles.rowCell}>
                        {template.model || '-'}
                      </TableCell>
                      <TableCell component="th" scope="row" align="center" className={styles.rowCell}>
                        <IconButton onClick={() => setOpenAddEdit(template.id)}>
                          <img src={EditIcon} alt="edit" />
                        </IconButton>
                      </TableCell>
                      <TableCell component="th" scope="row" align="center" className={styles.rowCell}>
                        <IconButton onClick={() => setDeleteId(template.id)}>
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        {deleteId && (
          <ErrorBox
            title={t`Delete Modbus Template`}
            error={t`Are you sure you want to delete ${templates[deleteId]?.name}`}
            onAccept={() => handleDelete()}
            onClose={() => setDeleteId(null)}
          />
        )}
        {openAddEdit && (
          <AddEditModbusTemplate
            templateId={openAddEdit !== "new" ? openAddEdit : undefined}
            onClose={() => setOpenAddEdit(null)}
            onSaved={onTemplateSaved}
          />
        )}
      </ServiceNavigationBar>
      {lookupAnchor && (
        <Lookup
          filtersList={TEMPLATE_TYPE_OPTIONS.map((option) => option.label)}
          appliedFilters={typeFilter}
          onApply={(selected: any) => {
            setTypeFilter(selected);
            setLookupAnchor(null);
          }}
          lookupAnchor={lookupAnchor}
          onClose={() => setLookupAnchor(null)}
          clearAllFilters={() => setTypeFilter([])}
          hasFilters={!!typeFilter.length}
        />
      )}
    </>
  );
};

export default ModbusTemplateManagement;
